import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * §v3 Liquid Glass — button primitive.
 *
 * Variants:
 *   primary — pale-glass-on-glass, dark-ink. The ONE visually-loud
 *             button per surface. Reads like the macOS "OK" default.
 *   accent  — pink→coral→amber gradient pill with a coral glow ring,
 *             dark-brown ink. Same recipe as the Overview tab's
 *             "View live calls" CTA — promote a CREATE action to
 *             feel like a primary call-to-action.
 *             §v3.accent-button (2026-05-12) added so New order /
 *             New ticket / New customer share the same visual rhythm
 *             as Overview's hero CTA.
 *   ghost   — chrome-color border, transparent fill. The everywhere
 *             else button. Use for nav, pill tabs, secondary actions.
 *   tonal   — glass-2 with hairline-strong border. Sits between
 *             primary and ghost — used for "do this thing" actions
 *             that aren't the page's primary CTA.
 *
 * Sizes: sm / md / lg follow the kit's 28 / 32 / 40 px height ramp.
 *
 * No ring on hover, no scale on press. The kit is explicit: micro-
 * animations like ring/scale read as fidgety on a laptop pointer.
 * Only color shifts via brightness + glass tint.
 */
export type GlassButtonVariant = "primary" | "accent" | "ghost" | "tonal";
export type GlassButtonSize = "sm" | "md" | "lg";

export interface GlassButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: GlassButtonVariant;
  size?: GlassButtonSize;
  loading?: boolean;
  /** Optional left-slot icon. */
  leadingIcon?: ReactNode;
  /** Optional right-slot icon. */
  trailingIcon?: ReactNode;
  children?: ReactNode;
}

const SIZE_CLASSES: Record<GlassButtonSize, string> = {
  sm: "h-7 px-2.5 text-g-caption gap-1.5 rounded-g-sm",
  md: "h-8 px-3 text-g-label gap-2 rounded-g-md",
  lg: "h-10 px-4 text-g-body gap-2 rounded-g-md",
};

const VARIANT_CLASSES: Record<GlassButtonVariant, string> = {
  primary:
    // Pale-glass gradient + dark ink, so it reads as the "default
    // action". Border is white/45 — it catches the canvas color the
    // way Apple's button on Sequoia does.
    cn(
      "text-fg-on-tint border",
      "shadow-rim",
      // Linear-gradient via inline style for the dual-stop alpha;
      // Tailwind's bg utility can't represent it cleanly.
    ),
  accent:
    // §v3.makeover — canonical magenta→coral accent pill (--accent-grad),
    // unified with .xp-btn.primary / ui Button so every primary CTA across
    // the dashboard reads the same. Dark ink #1b0a18 matches the canonical
    // primary. Force rounded-full + ink via inline className overrides
    // (rounded-* class beats the size class because it comes after in cn()
    // order). Background gradient + glow ring set via inline style — see
    // VARIANT_STYLE map below.
    cn(
      // §chrome-liquid — the ink was a literal #1b0a18 tuned for the old
      // magenta→coral pill. It now takes --accent-ink via VARIANT_STYLE
      // so it flips with the accent instead of staying near-black.
      "border-0 font-semibold rounded-full",
      "hover:brightness-105",
    ),
  ghost:
    // §chrome-liquid (2026-08-10) — was bg-transparent. A transparent
    // control is fine inside a card, but ghost buttons also sit in page
    // toolbars, which float directly on the console ground — and below
    // the sweep's periwinkle band that ground is a DARK surface. §07's
    // answer for chrome on the ground is a translucent white pill, so
    // ghost now carries the raised tier plus the card blur. Inside a
    // card it still reads as the quiet button; on the ground it reads
    // as a pill instead of vanishing.
    cn(
      "text-fg-2 border border-glass-hairline bg-glass-2",
      "backdrop-blur-soft backdrop-saturate-glass",
      "hover:text-fg-1 hover:bg-glass-0",
    ),
  tonal:
    cn(
      "text-fg-1 border border-glass-hairline-strong bg-glass-2",
      "backdrop-blur-soft backdrop-saturate-glass shadow-rim",
      "hover:bg-glass-3",
    ),
};

export const GlassButton = forwardRef<HTMLButtonElement, GlassButtonProps>(
  function GlassButton(
    {
      variant = "tonal",
      size = "md",
      loading = false,
      disabled,
      leadingIcon,
      trailingIcon,
      className,
      children,
      style,
      ...rest
    },
    ref,
  ) {
    const isPrimary = variant === "primary";
    const isAccent = variant === "accent";
    const variantStyle: React.CSSProperties | undefined = isPrimary
      ? {
          // §chrome-liquid — the primary button is the ACCENT, not a
          // white-alpha wash with a hardcoded dark label. On the light
          // theme the old recipe rendered near-white on near-white.
          background: "var(--accent)",
          color: "var(--accent-ink)",
          borderColor: "color-mix(in oklab, var(--accent) 78%, black 8%)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.22), 0 6px 18px -8px color-mix(in oklab, var(--accent) 70%, transparent)",
          ...style,
        }
      : isAccent
        ? {
            // §v3.makeover — canonical accent gradient, so every primary
            // CTA across the dashboard reads the same. §chrome-liquid: the
            // glow ring was struck in literal oklch magenta, which stayed
            // magenta after the palette moved — it now derives from
            // --accent so it follows the theme like the fill does.
            background: "var(--accent-grad)",
            color: "var(--accent-ink)",
            boxShadow:
              "0 0 0 1px color-mix(in oklab, var(--accent) 35%, transparent), 0 10px 32px -8px color-mix(in oklab, var(--accent) 45%, transparent), inset 0 1px 0 rgba(255,255,255,0.25)",
            ...style,
          }
        : style;
    return (
      <button
        ref={ref}
        type={rest.type ?? "button"}
        disabled={disabled || loading}
        className={cn(
          "inline-flex items-center justify-center font-medium",
          "transition-colors duration-g-fast ease-stand-glass",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-0",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "active:brightness-95",
          SIZE_CLASSES[size],
          VARIANT_CLASSES[variant],
          className,
        )}
        style={variantStyle}
        {...rest}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : leadingIcon ? (
          <span className="shrink-0" aria-hidden>{leadingIcon}</span>
        ) : null}
        {children}
        {!loading && trailingIcon ? (
          <span className="shrink-0" aria-hidden>{trailingIcon}</span>
        ) : null}
      </button>
    );
  },
);
